import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input } from "@/components/ui-kit";
import { formatINR } from "@/lib/format";
import { Plus, Trash2, Star, Edit3 } from "lucide-react";

const empty = {
  name: "", price: 0, duration_days: 30, vehicle_limit: 10, driver_limit: 5,
  user_limit: 3, booking_limit: 100, description: "", is_popular: false,
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => setPlans((await api.get("/admin/plans")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const data = { ...editing, price: Number(editing.price), duration_days: Number(editing.duration_days),
      vehicle_limit: Number(editing.vehicle_limit), driver_limit: Number(editing.driver_limit),
      user_limit: Number(editing.user_limit), booking_limit: Number(editing.booking_limit) };
    if (data.id) await api.patch(`/admin/plans/${data.id}`, data);
    else await api.post("/admin/plans", data);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    if (!confirm("Delete this plan?")) return;
    await api.delete(`/admin/plans/${id}`); load();
  };

  return (
    <div data-testid="admin-plans-page">
      <PageHeader
        title="Subscription Plans"
        subtitle="Define what tenants can buy. Limits, price (INR) and duration."
        actions={
          <Btn data-testid="plan-add-btn" onClick={() => { setEditing({ ...empty }); setOpen(true); }}>
            <Plus className="h-4 w-4" /> Add Plan
          </Btn>
        }
      />

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className={`bg-white rounded-lg p-6 ${p.is_popular ? "border-2 border-cab" : "border border-zinc-200"}`}>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-xl text-asphalt">{p.name}</div>
              {p.is_popular && <span className="badge bg-cab/15 text-asphalt"><Star className="h-3 w-3" /> Popular</span>}
            </div>
            <div className="text-sm text-zinc-500 mt-1">{p.description}</div>
            <div className="mt-4 font-display font-black text-3xl text-asphalt">{formatINR(p.price)}<span className="text-sm text-zinc-500 font-medium">/{p.duration_days}d</span></div>
            <ul className="mt-4 space-y-1 text-sm text-zinc-600">
              <li>• {p.vehicle_limit} vehicles</li>
              <li>• {p.driver_limit} drivers</li>
              <li>• {p.user_limit} users</li>
              <li>• {p.booking_limit.toLocaleString("en-IN")} bookings/mo</li>
            </ul>
            <div className="mt-5 flex gap-2">
              <Btn variant="outline" data-testid={`plan-edit-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }}>
                <Edit3 className="h-4 w-4" /> Edit
              </Btn>
              <Btn variant="ghost" data-testid={`plan-delete-${p.id}`} onClick={() => del(p.id)}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing?.id ? "Edit plan" : "New plan"}
        testid="plan-modal"
        footer={<>
          <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="plan-save-btn" onClick={submit}>{editing?.id ? "Save" : "Create"}</Btn>
        </>}
      >
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Name" required><Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} required data-testid="plan-name" /></Field>
            <Field label="Price (INR)" required><Input type="number" min="0" step="any" value={editing.price} onChange={(e) => setEditing({...editing, price: e.target.value})} required data-testid="plan-price" /></Field>
            <Field label="Duration (days)" required><Input type="number" value={editing.duration_days} onChange={(e) => setEditing({...editing, duration_days: e.target.value})} required /></Field>
            <Field label="Vehicle Limit" required><Input type="number" value={editing.vehicle_limit} onChange={(e) => setEditing({...editing, vehicle_limit: e.target.value})} required /></Field>
            <Field label="Driver Limit" required><Input type="number" value={editing.driver_limit} onChange={(e) => setEditing({...editing, driver_limit: e.target.value})} required /></Field>
            <Field label="User Limit" required><Input type="number" value={editing.user_limit} onChange={(e) => setEditing({...editing, user_limit: e.target.value})} required /></Field>
            <Field label="Booking Limit" required><Input type="number" value={editing.booking_limit} onChange={(e) => setEditing({...editing, booking_limit: e.target.value})} required /></Field>
            <Field label="Mark as popular">
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_popular} onChange={(e) => setEditing({...editing, is_popular: e.target.checked})} /> Highlight as Most Popular</label>
            </Field>
            <div className="col-span-2"><Field label="Description"><Input value={editing.description || ""} onChange={(e) => setEditing({...editing, description: e.target.value})} /></Field></div>
          </form>
        )}
      </Modal>
    </div>
  );
}
